import { useProgress } from "@utils/player";
import { usePlayerActions } from "@utils/player/core/usePlayer";
/* eslint-disable react-hooks/immutability -- Reanimated shared values are mutable refs by design in this drag surface. */
import LBox from "@components/LBox";
import { FlashList, type FlashListRef } from "@shopify/flash-list";
import * as Haptics from "expo-haptics";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  StyleSheet,
  type StyleProp,
  View,
  type ViewStyle,
} from "react-native";
import Animated, {
  FadeIn,
  FadeOut,
  runOnJS,
  useAnimatedStyle,
  useAnimatedReaction,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

import { getActiveTab } from "@hooks/useActiveTab";
import {
  useActiveCollectionName,
  useActiveCollectionId,
  useActiveCollectionType,
  useActiveQueue,
  useActiveSong,
} from "@utils/player";

import { selectActiveQueueItemId } from "@utils/player/core/selectors";
import { usePlayer } from "@utils/player/core/usePlayer";
import type { Song } from "@utils/player";
import { router } from "expo-router";
import { HeaderSWM } from "./HeaderSWM";
import { QueueDragHandleSWM } from "./QueueDragHandleSWM";
import { QueueRowSWM } from "./QueueRowSWM";
import { SongItemSWM, SongRenderItemSWM } from "./SongSWM";

type QueueSong = Song & { songQueueId: string };
type FlashListCellProps = React.ComponentProps<typeof View> & {
  index?: number;
  style?: StyleProp<ViewStyle>;
};

const songKeyExtractor = (song: QueueSong) => song.songQueueId;
const AUTO_SCROLL_EDGE_PX = 64;
const AUTO_SCROLL_STEP_PX = 12;
const DEFAULT_ROW_HEIGHT = 64;
const LIST_FOOTER_HEIGHT = 96;
const QUEUE_DRAG_OVERLAY_ENTERING = FadeIn.duration(90);
const QUEUE_DRAG_OVERLAY_EXITING = FadeOut.duration(90);
const QUEUE_DRAG_OVERLAY_VERTICAL_PADDING = 6;
const QUEUE_CONTENT_HORIZONTAL_INSET = 16;
const REORDER_ACTIVATION_DISTANCE_PX = 10;
const TOP_EDGE_DRAG_SCROLL_GUARD_OFFSET = 0.5;
const QUEUE_REORDER_ENABLED = Platform.OS !== "web";

const formatQueueSource = (
  collectionType: string | null | undefined,
  collectionName: string | null | undefined,
) => {
  if (collectionType === "foryou") return "For You";
  if (!collectionName) return null;
  if (collectionType === "playlist") return `Playlist: ${collectionName}`;
  if (collectionType === "album") return `Album: ${collectionName}`;
  if (collectionType === "artist") return `Artist: ${collectionName}`;
  return collectionName;
};

const sameQueueOrder = (a: QueueSong[], b: QueueSong[]) =>
  a.length === b.length &&
  a.every((song, index) => song.songQueueId === b[index]?.songQueueId);

const fireQueueDragHaptic = () => {
  Haptics.selectionAsync().catch(() => {});
};

interface TracksSWMProps {
  activeSetListExpanded: boolean;
  closeModal: () => void;
  onSetListEntryPress?: (songQueueId: string, startSeconds: number) => void;
  onSetListExpandedChange?: (expanded: boolean) => void;
  selectedSongQueueIds: string[];
  selectSongQueueId: (songQueueId: string) => void;
}

export const TracksSWM = ({
  activeSetListExpanded,
  closeModal,
  onSetListEntryPress,
  onSetListExpandedChange,
  selectedSongQueueIds,
  selectSongQueueId,
}: TracksSWMProps) => {
  const progressState = useProgress();

  const playerActions = usePlayerActions();

  const activeQueue = useActiveQueue();
  const activeQueueItemId = usePlayer(selectActiveQueueItemId);
  const activeSong = useActiveSong();
  const activeCollectionType = useActiveCollectionType();
  const activeCollectionId = useActiveCollectionId();
  const activeCollectionName = useActiveCollectionName();
  const playFromQueueItem = playerActions.playFromQueueItem;
  const setUpcomingQueueItemIds = playerActions.setUpcomingQueueItemIds;
  const [optimisticVisibleSongs, setOptimisticVisibleSongs] = useState<
    QueueSong[]
  >([]);
  const [dragBaseOrder, setDragBaseOrder] = useState<QueueSong[] | null>(null);
  const [dragOrder, setDragOrder] = useState<QueueSong[] | null>(null);
  const [draggingSong, setDraggingSong] = useState<QueueSong | null>(null);
  const [dragHasMoved, setDragHasMoved] = useState(false);
  const [draggedHeight, setDraggedHeight] = useState(DEFAULT_ROW_HEIGHT);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);

  const listRef = useRef<FlashListRef<QueueSong>>(null);
  const listDataRef = useRef<QueueSong[]>([]);
  const visibleSongsRef = useRef<QueueSong[]>([]);
  const dragBaseOrderRef = useRef<QueueSong[] | null>(null);
  const dragOrderRef = useRef<QueueSong[] | null>(null);
  const draggingSongRef = useRef<QueueSong | null>(null);
  const cellLayoutsRef = useRef(
    new Map<string, { height: number; top: number }>(),
  );
  const rowHeightsRef = useRef(new Map<string, number>());
  const measuredRowHeightCountRef = useRef(0);
  const measuredRowHeightTotalRef = useRef(0);
  const averageRowHeightRef = useRef(DEFAULT_ROW_HEIGHT);
  const headerHeightRef = useRef(0);
  const viewportHeightRef = useRef(0);
  const contentHeightRef = useRef(0);
  const scrollOffsetRef = useRef(0);
  const dragItemStartContentYRef = useRef(0);
  const dragStartScrollOffsetRef = useRef(0);
  const draggedHeightRef = useRef(DEFAULT_ROW_HEIGHT);
  const latestTranslationYRef = useRef(0);
  const latestTopContentRef = useRef(0);
  const dragCanReorderRef = useRef(false);
  const topEdgeDragGuardAppliedRef = useRef(false);
  const autoScrollDirectionRef = useRef(-0 as -1 | 0 | 1);
  const autoScrollFrameRef = useRef<number | null>(null);
  const autoScrollTickRef = useRef<() => void>(() => {});
  const updateDragRef = useRef<(translationY: number) => void>(() => {});
  const dragOverlayTop = useSharedValue(0);
  const dragItemStartContentYShared = useSharedValue(0);
  const dragStartScrollOffsetShared = useSharedValue(0);
  const dragOverlayAnimatedStyle = useAnimatedStyle(() => ({
    top: dragOverlayTop.value - QUEUE_DRAG_OVERLAY_VERTICAL_PADDING,
  }));

  const visibleSongs: QueueSong[] = useMemo(() => {
    if (!activeQueue || activeQueue.currentIndex === -1) return [];

    const upcomingItems = activeQueue.items.slice(activeQueue.currentIndex + 1);
    const upcoming = upcomingItems.map((item) => ({
      ...item.metadata,
      songQueueId: item.qid,
    })) as QueueSong[];

    if (upcoming.length === optimisticVisibleSongs.length) {
      const optimisticVisibleSongQueueIds = optimisticVisibleSongs.map(
        (song) => song.songQueueId,
      );

      if (
        upcoming.every((song) =>
          optimisticVisibleSongQueueIds.includes(song.songQueueId),
        )
      ) {
        return optimisticVisibleSongs;
      }
    }

    return upcoming;
  }, [activeQueue, optimisticVisibleSongs]);

  useEffect(() => {
    visibleSongsRef.current = visibleSongs;

    if (!draggingSongRef.current) {
      dragOrderRef.current = null;
      setDragOrder(null);
    }
  }, [visibleSongs]);

  const getRowHeight = useCallback((songQueueId: string) => {
    return (
      rowHeightsRef.current.get(songQueueId) ?? averageRowHeightRef.current
    );
  }, []);

  const getMeasuredItemContentTop = useCallback((songQueueId: string) => {
    const cellLayout = cellLayoutsRef.current.get(songQueueId);

    if (cellLayout?.top === undefined) {
      return null;
    }

    return headerHeightRef.current + cellLayout.top;
  }, []);

  const getContentHeight = useCallback(
    (order: QueueSong[]) =>
      headerHeightRef.current +
      order.reduce((total, song) => total + getRowHeight(song.songQueueId), 0) +
      LIST_FOOTER_HEIGHT,
    [getRowHeight],
  );

  const getItemContentTop = useCallback(
    (order: QueueSong[], songQueueId: string) => {
      let top = headerHeightRef.current;

      for (const song of order) {
        if (song.songQueueId === songQueueId) {
          return top;
        }

        top += getRowHeight(song.songQueueId);
      }

      return top;
    },
    [getRowHeight],
  );

  const setDragOrderState = useCallback(
    (order: QueueSong[] | null) => {
      const previousOrderLength = dragOrderRef.current?.length;

      dragOrderRef.current = order;
      setDragOrder(order);

      if (order && previousOrderLength !== order.length) {
        contentHeightRef.current = getContentHeight(order);
      }
    },
    [getContentHeight],
  );

  const setDragBaseOrderState = useCallback((order: QueueSong[] | null) => {
    dragBaseOrderRef.current = order;
    setDragBaseOrder(order);
  }, []);

  const setDraggingSongState = useCallback((song: QueueSong | null) => {
    draggingSongRef.current = song;
    setDraggingSong(song);
  }, []);

  const stopAutoScroll = useCallback(() => {
    autoScrollDirectionRef.current = 0;
    setIsAutoScrolling(false);

    if (autoScrollFrameRef.current !== null) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
  }, []);

  const getMaxScrollOffset = useCallback(
    () => Math.max(0, contentHeightRef.current - viewportHeightRef.current),
    [],
  );

  const scheduleAutoScrollTick = useCallback(() => {
    if (autoScrollFrameRef.current !== null) {
      return;
    }

    autoScrollFrameRef.current = requestAnimationFrame(() => {
      autoScrollFrameRef.current = null;
      autoScrollTickRef.current();
    });
  }, []);

  const autoScrollTick = useCallback(() => {
    const direction = autoScrollDirectionRef.current;
    const order = dragOrderRef.current;

    if (!direction || !draggingSongRef.current || !order) {
      return;
    }

    const maxOffset = getMaxScrollOffset();
    const nextOffset = Math.max(
      0,
      Math.min(
        maxOffset,
        scrollOffsetRef.current + direction * AUTO_SCROLL_STEP_PX,
      ),
    );

    if (nextOffset === scrollOffsetRef.current) {
      stopAutoScroll();
      return;
    }

    scrollOffsetRef.current = nextOffset;
    listRef.current?.scrollToOffset({ animated: false, offset: nextOffset });
    updateDragRef.current(latestTranslationYRef.current);

    scheduleAutoScrollTick();
  }, [getMaxScrollOffset, scheduleAutoScrollTick, stopAutoScroll]);

  useEffect(() => {
    autoScrollTickRef.current = autoScrollTick;
  }, [autoScrollTick]);

  const ensureAutoScroll = useCallback(() => {
    if (autoScrollDirectionRef.current) {
      setIsAutoScrolling(true);
      scheduleAutoScrollTick();
    }
  }, [scheduleAutoScrollTick]);

  const setAutoScrollDirection = useCallback(
    (direction: -1 | 1) => {
      const maxOffset = getMaxScrollOffset();
      const canScroll =
        direction === -1
          ? scrollOffsetRef.current > TOP_EDGE_DRAG_SCROLL_GUARD_OFFSET
          : scrollOffsetRef.current < maxOffset;

      if (!canScroll) {
        stopAutoScroll();
        return;
      }

      autoScrollDirectionRef.current = direction;
      ensureAutoScroll();
    },
    [ensureAutoScroll, getMaxScrollOffset, stopAutoScroll],
  );

  const updateDrag = useCallback(
    (translationY: number) => {
      const order = dragOrderRef.current;
      const song = draggingSongRef.current;

      if (!order || !song) return;

      const scrollDelta =
        scrollOffsetRef.current - dragStartScrollOffsetRef.current;
      const nextTopContent =
        dragItemStartContentYRef.current + translationY + scrollDelta;
      const previousTopContent = latestTopContentRef.current;
      const rowHeight = draggedHeightRef.current;
      const nextOverlayTop = nextTopContent - scrollOffsetRef.current;
      const isMovingUp = nextTopContent < previousTopContent;
      const isMovingDown = nextTopContent > previousTopContent;

      latestTranslationYRef.current = translationY;
      latestTopContentRef.current = nextTopContent;

      dragOverlayTop.value = nextOverlayTop;

      if (!dragCanReorderRef.current) {
        if (Math.abs(translationY) < REORDER_ACTIVATION_DISTANCE_PX) {
          stopAutoScroll();
          return;
        }

        dragCanReorderRef.current = true;
      }

      const currentIndex = order.findIndex(
        (candidate) => candidate.songQueueId === song.songQueueId,
      );
      let targetIndex = currentIndex;

      if (currentIndex !== -1 && isMovingUp) {
        while (targetIndex > 0) {
          const previousSong = order[targetIndex - 1];
          const previousMidpoint =
            getItemContentTop(order, previousSong.songQueueId) +
            getRowHeight(previousSong.songQueueId) / 2;

          if (nextTopContent >= previousMidpoint) {
            break;
          }

          targetIndex -= 1;
        }
      } else if (currentIndex !== -1 && isMovingDown) {
        const nextBottomContent = nextTopContent + rowHeight;

        while (targetIndex < order.length - 1) {
          const nextSong = order[targetIndex + 1];
          const nextMidpoint =
            getItemContentTop(order, nextSong.songQueueId) +
            getRowHeight(nextSong.songQueueId) / 2;

          if (nextBottomContent <= nextMidpoint) {
            break;
          }

          targetIndex += 1;
        }
      }

      if (currentIndex !== -1 && targetIndex !== currentIndex) {
        const nextOrder = [...order];

        nextOrder.splice(currentIndex, 1);
        nextOrder.splice(targetIndex, 0, song);
        setDragOrderState(nextOrder);
        fireQueueDragHaptic();
      }

      if (nextOverlayTop < AUTO_SCROLL_EDGE_PX) {
        setAutoScrollDirection(-1);
      } else if (
        nextOverlayTop + rowHeight >
        viewportHeightRef.current - AUTO_SCROLL_EDGE_PX
      ) {
        setAutoScrollDirection(1);
      } else {
        stopAutoScroll();
      }
    },
    [
      getItemContentTop,
      getRowHeight,
      setDragOrderState,
      setAutoScrollDirection,
      dragOverlayTop,
      stopAutoScroll,
    ],
  );

  useEffect(() => {
    updateDragRef.current = updateDrag;
  }, [updateDrag]);

  const headerPressable = useMemo(
    () =>
      activeCollectionType &&
      activeCollectionType !== "foryou" &&
      activeCollectionId
        ? {
            onPress: () => {
              const activeTab = getActiveTab();
              switch (activeCollectionType) {
                case "album":
                case "playlist":
                  router.navigate({
                    pathname: `/(app)/(tabs)/${activeTab}/[collectionType]/[collectionId]`,
                    params: {
                      collectionId: activeCollectionId,
                      collectionType: activeCollectionType,
                    },
                  });
                  closeModal();
                  break;
                case "artist":
                  router.navigate({
                    pathname: `/(app)/(tabs)/${activeTab}/artist/[artistId]`,
                    params: {
                      artistId: activeCollectionId,
                      collectionType: activeCollectionType,
                    },
                  });
                  closeModal();
                  break;
              }
            },
            sentryEventName: "tracks_header_press_swm",
          }
        : undefined,
    [activeCollectionId, activeCollectionType, closeModal],
  );

  const playingFromPressable = useMemo(
    () =>
      activeCollectionName && headerPressable
        ? {
            onPress: headerPressable.onPress,
            sentryEventName: "playing_from_header_press",
          }
        : undefined,
    [activeCollectionName, headerPressable],
  );

  const queueSourceLabel = useMemo(
    () => formatQueueSource(activeCollectionType, activeCollectionName),
    [activeCollectionName, activeCollectionType],
  );

  const handlePlay = useCallback(
    (songQueueId: string) => {
      playFromQueueItem(songQueueId, { autoPlay: true });
    },
    [playFromQueueItem],
  );

  const handleSelect = useCallback(
    (songQueueId: string) => {
      selectSongQueueId(songQueueId);
    },
    [selectSongQueueId],
  );

  const currentSongIsSetList =
    activeSong?.isSetList && (activeSong?.setList?.entries?.length ?? 0) > 0;

  const currentSetListEntries = currentSongIsSetList
    ? (activeSong?.setList?.entries ?? null)
    : null;

  const [activeSetlistIndex, setActiveSetlistIndex] = useState(-1);
  const setListStartSecondsShared = useSharedValue<number[]>([]);

  const [prevSetListEntries, setPrevSetListEntries] = useState(
    currentSetListEntries,
  );
  if (prevSetListEntries !== currentSetListEntries) {
    setPrevSetListEntries(currentSetListEntries);
    setActiveSetlistIndex(-1);
  }

  useEffect(() => {
    setListStartSecondsShared.value = currentSetListEntries
      ? currentSetListEntries.map((e) => e.startSeconds)
      : [];
  }, [currentSetListEntries, setListStartSecondsShared]);

  useAnimatedReaction(
    () => {
      "worklet";

      const starts = setListStartSecondsShared.value;
      if (starts.length === 0) return -1;

      const position = progressState.position.value;

      for (let i = starts.length - 1; i >= 0; i--) {
        if (position >= starts[i]) {
          return i;
        }
      }

      return -1;
    },
    (nextIndex, prevIndex) => {
      if (nextIndex !== prevIndex) {
        runOnJS(setActiveSetlistIndex)(nextIndex);
      }
    },
    [setListStartSecondsShared],
  );

  const beginDrag = useCallback(
    (song: QueueSong) => {
      const order = dragOrderRef.current ?? visibleSongsRef.current;
      const cellLayout = cellLayoutsRef.current.get(song.songQueueId);
      const rowHeight = cellLayout?.height ?? getRowHeight(song.songQueueId);
      const itemContentTop =
        getMeasuredItemContentTop(song.songQueueId) ??
        getItemContentTop(order, song.songQueueId);

      latestTranslationYRef.current = 0;
      latestTopContentRef.current = itemContentTop;
      draggedHeightRef.current = rowHeight;
      dragItemStartContentYRef.current = itemContentTop;
      dragStartScrollOffsetRef.current = scrollOffsetRef.current;
      dragItemStartContentYShared.value = itemContentTop;
      dragStartScrollOffsetShared.value = scrollOffsetRef.current;
      dragCanReorderRef.current = false;

      setDragHasMoved(true);
      setDraggedHeight(rowHeight);
      dragOverlayTop.value = itemContentTop - scrollOffsetRef.current;
      setDragBaseOrderState(order);
      setDraggingSongState(song);
      setDragOrderState(order);
      fireQueueDragHaptic();
    },
    [
      getItemContentTop,
      getMeasuredItemContentTop,
      getRowHeight,
      dragOverlayTop,
      dragItemStartContentYShared,
      dragStartScrollOffsetShared,
      setDragBaseOrderState,
      setDragOrderState,
      setDraggingSongState,
    ],
  );

  const restoreTopEdgeQueueDragGuard = useCallback(() => {
    if (!topEdgeDragGuardAppliedRef.current || draggingSongRef.current) {
      return;
    }

    topEdgeDragGuardAppliedRef.current = false;

    if (scrollOffsetRef.current > TOP_EDGE_DRAG_SCROLL_GUARD_OFFSET + 0.25) {
      return;
    }

    scrollOffsetRef.current = 0;
    dragStartScrollOffsetShared.value = 0;
    listRef.current?.scrollToOffset({ animated: false, offset: 0 });
  }, [dragStartScrollOffsetShared]);

  const completeDrag = useCallback(() => {
    const order = dragOrderRef.current;
    const originalOrder = visibleSongsRef.current;

    if (order && !sameQueueOrder(originalOrder, order)) {
      setUpcomingQueueItemIds(order.map((song) => song.songQueueId));
      setOptimisticVisibleSongs(order);
    }

    latestTranslationYRef.current = 0;
    latestTopContentRef.current = 0;
    dragCanReorderRef.current = false;
    setDragHasMoved(false);
    setDragBaseOrderState(null);
    setDraggingSongState(null);
    setDragOrderState(null);
    restoreTopEdgeQueueDragGuard();
  }, [
    restoreTopEdgeQueueDragGuard,
    setDragBaseOrderState,
    setDragOrderState,
    setDraggingSongState,
    setUpcomingQueueItemIds,
  ]);

  const finishDrag = useCallback(() => {
    const order = dragOrderRef.current;
    const song = draggingSongRef.current;
    const hadActiveDrag = !!song;

    stopAutoScroll();

    if (hadActiveDrag) {
      fireQueueDragHaptic();
    }

    if (order && song) {
      const targetOverlayTop =
        getItemContentTop(order, song.songQueueId) - scrollOffsetRef.current;

      dragOverlayTop.value = withTiming(
        targetOverlayTop,
        { duration: 120 },
        (finished) => {
          if (finished) {
            runOnJS(completeDrag)();
          }
        },
      );
      return;
    }

    completeDrag();
  }, [completeDrag, dragOverlayTop, getItemContentTop, stopAutoScroll]);

  const handleContainerLayout = useCallback((event: LayoutChangeEvent) => {
    viewportHeightRef.current = event.nativeEvent.layout.height;
  }, []);

  const handleHeaderLayout = useCallback(
    (event: LayoutChangeEvent) => {
      headerHeightRef.current = event.nativeEvent.layout.height;
      contentHeightRef.current = getContentHeight(
        dragOrderRef.current ?? visibleSongsRef.current,
      );
    },
    [getContentHeight],
  );

  const handleRowLayout = useCallback(
    (songQueueId: string, event: LayoutChangeEvent) => {
      const nextHeight = event.nativeEvent.layout.height;

      if (nextHeight > 0) {
        const previousHeight = rowHeightsRef.current.get(songQueueId);

        rowHeightsRef.current.set(songQueueId, nextHeight);

        if (previousHeight === undefined) {
          measuredRowHeightCountRef.current += 1;
          measuredRowHeightTotalRef.current += nextHeight;
        } else {
          measuredRowHeightTotalRef.current += nextHeight - previousHeight;
        }

        averageRowHeightRef.current =
          measuredRowHeightTotalRef.current / measuredRowHeightCountRef.current;

        contentHeightRef.current = getContentHeight(
          dragOrderRef.current ?? visibleSongsRef.current,
        );
      }
    },
    [getContentHeight],
  );

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollOffsetRef.current = event.nativeEvent.contentOffset.y;
    },
    [],
  );

  const guardTopEdgeQueueDrag = useCallback(() => {
    if (
      scrollOffsetRef.current > 0 ||
      (contentHeightRef.current > 0 &&
        contentHeightRef.current <= viewportHeightRef.current)
    ) {
      return;
    }

    scrollOffsetRef.current = TOP_EDGE_DRAG_SCROLL_GUARD_OFFSET;
    topEdgeDragGuardAppliedRef.current = true;
    dragStartScrollOffsetShared.value = TOP_EDGE_DRAG_SCROLL_GUARD_OFFSET;
    listRef.current?.scrollToOffset({
      animated: false,
      offset: TOP_EDGE_DRAG_SCROLL_GUARD_OFFSET,
    });
  }, [dragStartScrollOffsetShared]);

  const renderDragHandle = useCallback(
    (song: QueueSong) =>
      QUEUE_REORDER_ENABLED ? (
        <QueueDragHandleSWM
          onBegin={() => beginDrag(song)}
          onTouchEnd={restoreTopEdgeQueueDragGuard}
          onTouchStart={guardTopEdgeQueueDrag}
          dragItemStartContentY={dragItemStartContentYShared}
          dragOverlayTop={dragOverlayTop}
          dragStartScrollOffset={dragStartScrollOffsetShared}
          onEnd={finishDrag}
          onMove={updateDrag}
        />
      ) : (
        false
      ),
    [
      beginDrag,
      dragItemStartContentYShared,
      dragOverlayTop,
      dragStartScrollOffsetShared,
      finishDrag,
      guardTopEdgeQueueDrag,
      restoreTopEdgeQueueDragGuard,
      updateDrag,
    ],
  );

  const renderItem = useCallback(
    ({ item }: { item: QueueSong }) => {
      const isDraggingItem =
        dragHasMoved && draggingSong?.songQueueId === item.songQueueId;
      const baseOrder = dragBaseOrder ?? visibleSongsRef.current;
      const rowTranslateY =
        dragHasMoved && dragOrder && !isDraggingItem
          ? getItemContentTop(dragOrder, item.songQueueId) -
            getItemContentTop(baseOrder, item.songQueueId)
          : 0;

      return (
        <QueueRowSWM
          isDraggingItem={isDraggingItem}
          onLayout={(event) => handleRowLayout(item.songQueueId, event)}
          shouldAnimate={dragHasMoved && !isAutoScrolling}
          translateY={rowTranslateY}
        >
          <SongRenderItemSWM
            dragHandle={renderDragHandle(item)}
            item={item}
            onPlay={handlePlay}
            onSetListEntryPress={onSetListEntryPress}
            onSetListExpandedChange={onSetListExpandedChange}
            select={handleSelect}
            selected={selectedSongQueueIds.includes(item.songQueueId)}
          />
        </QueueRowSWM>
      );
    },
    [
      dragBaseOrder,
      dragHasMoved,
      dragOrder,
      draggingSong?.songQueueId,
      getItemContentTop,
      handlePlay,
      handleRowLayout,
      handleSelect,
      isAutoScrolling,
      onSetListEntryPress,
      onSetListExpandedChange,
      renderDragHandle,
      selectedSongQueueIds,
    ],
  );

  const listHeader = useMemo(
    () => (
      <LBox onLayout={handleHeaderLayout}>
        {currentSongIsSetList && activeSong ? (
          <LBox marginTop={activeSetListExpanded ? "s2" : "zero"}>
            <SongItemSWM
              activeSetlistIndex={activeSetlistIndex}
              expanded={activeSetListExpanded}
              isCurrentSong
              onPlay={handlePlay}
              onSetListEntryPress={onSetListEntryPress}
              onSetListExpandedChange={onSetListExpandedChange}
              select={handleSelect}
              selected={
                !!activeQueueItemId &&
                selectedSongQueueIds.includes(activeQueueItemId)
              }
              showSongRow={false}
              song={
                {
                  ...(activeSong as Song),
                  songQueueId: activeQueueItemId ?? "",
                } as QueueSong
              }
            />
          </LBox>
        ) : null}
        <HeaderSWM
          pressable={headerPressable}
          rightPressable={playingFromPressable}
          rightText={queueSourceLabel}
          text="Up next"
        />
      </LBox>
    ),
    [
      activeQueueItemId,
      activeSetListExpanded,
      activeSetlistIndex,
      activeSong,
      currentSongIsSetList,
      handleHeaderLayout,
      handlePlay,
      handleSelect,
      headerPressable,
      onSetListEntryPress,
      onSetListExpandedChange,
      playingFromPressable,
      queueSourceLabel,
      selectedSongQueueIds,
    ],
  );

  const listData = draggingSong
    ? (dragBaseOrder ?? visibleSongs)
    : visibleSongs;

  useEffect(() => {
    listDataRef.current = listData;
  }, [listData]);

  const listExtraData = useMemo(
    () => ({
      dragBaseOrder,
      dragHasMoved,
      dragOrder,
      draggedHeight,
      draggingSongQueueId: draggingSong?.songQueueId,
      selectedSongQueueIds,
    }),
    [
      dragBaseOrder,
      dragHasMoved,
      dragOrder,
      draggedHeight,
      draggingSong?.songQueueId,
      selectedSongQueueIds,
    ],
  );

  /* eslint-disable react-hooks/refs -- FlashList cell renderer records native layout metadata in refs; it does not affect render output. */
  const CellRendererComponent = useMemo(
    () =>
      React.forwardRef<View, FlashListCellProps>(function QueueCellRenderer(
        { index, style, ...props },
        ref,
      ) {
        const flattenedStyle = StyleSheet.flatten(style);
        const song =
          typeof index === "number" ? listDataRef.current[index] : undefined;
        const top = flattenedStyle?.top;

        if (song && typeof top === "number") {
          cellLayoutsRef.current.set(song.songQueueId, {
            height:
              rowHeightsRef.current.get(song.songQueueId) ??
              averageRowHeightRef.current,
            top,
          });
        }

        return <View ref={ref} {...props} style={style} />;
      }),
    [],
  );
  /* eslint-enable react-hooks/refs */

  return (
    <View onLayout={handleContainerLayout} style={{ flex: 1 }}>
      <FlashList
        CellRendererComponent={CellRendererComponent}
        ref={listRef}
        data={listData}
        extraData={listExtraData}
        keyExtractor={songKeyExtractor}
        ListFooterComponent={<LBox height={LIST_FOOTER_HEIGHT} />}
        ListHeaderComponent={listHeader}
        maintainVisibleContentPosition={{ disabled: true }}
        onScroll={handleScroll}
        renderItem={renderItem}
        scrollEnabled={!draggingSong}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
      />
      {dragHasMoved && draggingSong ? (
        <Animated.View
          entering={QUEUE_DRAG_OVERLAY_ENTERING}
          exiting={QUEUE_DRAG_OVERLAY_EXITING}
          pointerEvents="none"
          style={[
            {
              left: -QUEUE_CONTENT_HORIZONTAL_INSET,
              opacity: 0.96,
              position: "absolute",
              right: -QUEUE_CONTENT_HORIZONTAL_INSET,
              zIndex: 20,
            },
            dragOverlayAnimatedStyle,
          ]}
        >
          <LBox
            backgroundColor="whiteTranslucent80"
            style={{
              paddingBottom: QUEUE_DRAG_OVERLAY_VERTICAL_PADDING,
              paddingHorizontal: QUEUE_CONTENT_HORIZONTAL_INSET,
              paddingTop: QUEUE_DRAG_OVERLAY_VERTICAL_PADDING,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: 0.18,
              shadowRadius: 18,
            }}
          >
            <SongItemSWM
              dragHandle={renderDragHandle(draggingSong)}
              noBottomMargin
              onPlay={handlePlay}
              onSetListEntryPress={onSetListEntryPress}
              onSetListExpandedChange={onSetListExpandedChange}
              select={handleSelect}
              selected={selectedSongQueueIds.includes(draggingSong.songQueueId)}
              song={draggingSong}
            />
          </LBox>
        </Animated.View>
      ) : null}
    </View>
  );
};
